import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Utensils, SplitSquareHorizontal, HandCoins, Gift, ShieldCheck, ArrowRight, Plus, Minus } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { PaymentPreview } from "@/components/demos/PaymentPreview";
import { SplitBillDemo } from "@/components/demos/SplitBillDemo";
import { TippingDemo } from "@/components/demos/TippingDemo";

const comingSoon = () =>
  toast("MONOE is coming soon.", { description: "Launching on the App Store and Google Play. Thanks for your interest!" });

const FEATURES = [
  { id: "discover", icon: Search, title: "Discover", desc: "Search participating businesses, browse categories, filter by pickup, dine-in, waitlist, or reservations." },
  { id: "order", icon: Utensils, title: "Order your way", desc: "Order ahead for pickup or scan a table QR to order from your seat." },
  { id: "split", icon: SplitSquareHorizontal, title: "Split any bill", desc: "Pay the whole table, only your items, split evenly, or pick specific items." },
  { id: "tip", icon: HandCoins, title: "Tip 100%", desc: "MONOE takes 0% of tips; workers keep every cent, routed to the assigned server." },
  { id: "rewards", icon: Gift, title: "Dollar rewards", desc: "Merchant-funded dollar rewards, plus birthday rewards where offered. No points, no wallet." },
  { id: "secure", icon: ShieldCheck, title: "Secure by Stripe", desc: "Payments processed by Stripe. MONOE never stores your full card number or CVV." },
];

const CUSTOMER_STEPS = [
  "Discover a business.",
  "Order or reserve.",
  "Split & tip (100% to staff).",
  "Pay securely, get your receipt.",
  "Earn rewards and come back.",
];

const BUSINESS_STEPS = [
  "Join and set up your menu/catalog.",
  "Get discovered.",
  "Take orders & payments (route to your POS where connected).",
  "Reward loyalty with optional dollar & birthday rewards.",
  "Grow with analytics and multi-location tools.",
];

const FAQ = [
  ["What is MONOE?", "An app to discover local businesses, order or reserve, split the bill, tip, pay, and earn dollar rewards."],
  ["Is MONOE a bank?", "No. MONOE is a commerce platform; payments are processed by Stripe."],
  ["Do I need a MONOE wallet?", "No — there is no MONOE wallet or stored balance."],
  ["How do MONOE Rewards work?", "Participating businesses offer dollar rewards you redeem toward future eligible purchases."],
  ["Are MONOE Rewards points?", "No. Rewards are dollars, never points."],
  ["Can I withdraw rewards?", "No. Rewards aren't cash and can't be withdrawn or transferred."],
  ["Who pays for rewards?", "The participating business funds its own rewards (including birthday). MONOE doesn't fund them."],
  ["What are Birthday Rewards?", "An optional, business-funded reward during your birthday month. The business only sees that you're eligible — never your full birthday."],
  ["How does Split Bill work?", "On a shared table tab you can pay the whole table, only your items, split evenly, or pick specific items."],
  ["Do tips get taken by MONOE?", "No — MONOE takes 0% of tips; workers receive 100%."],
  ["Can businesses turn Rewards off?", "Yes. Rewards are optional and off unless a business turns them on."],
  ["Does MONOE work with my POS?", "Square and Clover are supported today; Toast is in progress."],
  ["Can franchises use MONOE?", "Yes — MONOE supports Brand → Organization → Location with combined reporting."],
  ["How does MONOE handle payments?", "Securely through Stripe; MONOE doesn't store full card numbers or CVV."],
  ["How do businesses join MONOE?", "Create a business account in the app, set up your menu/catalog, and start taking orders and payments."],
];

const FLOW_STEPS = ["Discover", "Business", "Order/Reserve", "Split", "Tip", "Pay", "Rewards", "Receipt"];

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <div className="mt-10 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {FAQ.map(([q, a], i) => (
        <div key={q}>
          <button
            type="button"
            data-testid={`faq-item-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-secondary"
          >
            <span className="text-sm font-semibold text-foreground">{q}</span>
            {open === i ? <Minus className="h-4 w-4 shrink-0" /> : <Plus className="h-4 w-4 shrink-0" />}
          </button>
          {open === i && <p className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">{a}</p>}
        </div>
      ))}
    </div>
  );
};

const Steps = ({ title, steps, testId }) => (
  <div data-testid={testId} className="rounded-lg border border-border bg-card p-8">
    <h3 className="font-display text-lg font-semibold">{title}</h3>
    <ol className="mt-5 space-y-3">
      {steps.map((s, i) => (
        <li key={s} className="flex items-start gap-3">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-foreground font-mono-ui text-[11px] text-background">{i + 1}</span>
          <span className="text-sm leading-relaxed text-muted-foreground">{s}</span>
        </li>
      ))}
    </ol>
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0 grid-bg opacity-60 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
    <div className="pointer-events-none absolute inset-0 grain" />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pb-28 lg:pt-24">
      <div className="flex flex-col justify-center">
        <Reveal>
          <Eyebrow>The space where business meets life.</Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Discover local.
            <br />
            Pay smarter.
            <br />
            Get rewarded.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
            Discover businesses near you, order or reserve, split the bill, tip, pay, and earn
            dollar rewards — all in one app.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="hero-get-app-button"
              onClick={comingSoon}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-6 py-3 text-sm font-semibold text-background transition-transform duration-200 hover:-translate-y-0.5"
            >
              Get the app
            </button>
            <Link
              to="/business"
              data-testid="hero-for-business-button"
              className="inline-flex items-center gap-2 rounded-md border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
            >
              For business <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <span className="mt-8 inline-flex w-fit items-center gap-2 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-foreground" /> In development · preparing for launch
          </span>
        </Reveal>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="flex items-center"
      >
        <div className="w-full max-w-md lg:ml-auto">
          <PaymentPreview />
        </div>
      </motion.div>
    </div>
  </section>
);

const WhatIsMonoe = () => (
  <section className="border-t border-border">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <Reveal>
        <Eyebrow>What is MONOE</Eyebrow>
        <p className="mt-4 max-w-3xl font-display text-xl font-semibold leading-snug tracking-tight sm:text-2xl">
          MONOE is one app for the local businesses around you. Discover a place, order ahead or
          at your table, split the bill any way you like, tip, and pay securely — then earn dollar
          rewards from businesses that offer them.
        </p>
      </Reveal>
      <Reveal delay={0.08}>
        <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-2 font-mono-ui text-xs text-muted-foreground">
          {FLOW_STEPS.map((s, i, arr) => (
            <span key={s} className="flex items-center gap-2">
              <span className="rounded-full border border-border bg-card px-3 py-1.5 text-foreground">{s}</span>
              {i < arr.length - 1 && <span>→</span>}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const FeatureGrid = () => (
  <section className="border-t border-border bg-secondary/40">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f, i) => (
          <Reveal key={f.id} delay={i * 0.04}>
            <div data-testid={`feature-${f.id}-card`} className="group h-full bg-card p-6 transition-colors hover:bg-secondary sm:p-8">
              <div className="grid h-11 w-11 place-items-center rounded-lg border border-border bg-background text-foreground transition-transform duration-200 group-hover:-translate-y-0.5">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="border-t border-border">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <Reveal>
        <Eyebrow>How it works</Eyebrow>
      </Reveal>
      <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:gap-10">
        <Reveal><Steps title="For customers" steps={CUSTOMER_STEPS} testId="how-customers" /></Reveal>
        <Reveal delay={0.06}><Steps title="For businesses" steps={BUSINESS_STEPS} testId="how-businesses" /></Reveal>
      </div>
    </div>
  </section>
);

const SeeItWork = () => (
  <section className="border-t border-border bg-secondary/40">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <Reveal>
        <Eyebrow>See it work</Eyebrow>
        <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl">
          Split any bill and tip 100% — every guest pays the merchant directly.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 lg:grid-cols-2 lg:gap-10">
        <Reveal><SplitBillDemo /></Reveal>
        <Reveal delay={0.08}><TippingDemo /></Reveal>
      </div>
    </div>
  </section>
);

const FaqSection = () => (
  <section className="border-t border-border">
    <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <Reveal>
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">Questions, answered.</h2>
      </Reveal>
      <Reveal delay={0.06}><Faq /></Reveal>
    </div>
  </section>
);

const CtaBanner = () => (
  <section className="border-t border-border">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <Reveal>
        <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-border bg-foreground p-8 text-background sm:p-12 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Bring MONOE to your customers.</h2>
            <p className="mt-3 max-w-lg text-sm text-background/70">Get discovered, take orders and payments, and reward loyalty — all in one platform.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={comingSoon} className="rounded-md bg-background px-6 py-3 text-sm font-semibold text-foreground">Get the app</button>
            <Link to="/business" className="rounded-md border border-background/30 px-6 py-3 text-sm font-semibold text-background">For business</Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  useSeo({
    title: "The space where business meets life.",
    description: "Discover businesses near you, order or reserve, split the bill, tip, pay, and earn dollar rewards — all in one app.",
    path: "/",
  });

  return (
    <div>
      <Hero />
      <WhatIsMonoe />
      <FeatureGrid />
      <HowItWorks />
      <SeeItWork />
      <FaqSection />
      <CtaBanner />
    </div>
  );
};

export default Home;
